/**
 * A dashboard tile. When `meta` is given it takes that expiry state's colour,
 * so the tiles across the top of the dashboard read as the same ramp the rows
 * below them use.
 *
 * Rendered as a real <button> when it links somewhere — twreact-ui's Card is
 * always a <div>, and a clickable div is invisible to the keyboard. A tile that
 * navigates has to be reachable by Tab like any other control.
 *
 * `urgent` adds the slow pulse from index.css — reserved for a non-zero expired
 * or critical count, never more than that. If everything pulses, nothing does.
 */
export default function StatTile({
  label,
  value,
  hint,
  icon = null,
  meta = null,
  urgent = false,
  onClick = null,
  active = false,
}) {
  const Element = onClick ? 'button' : 'div';

  const tint = meta
    ? { color: meta.fg, background: meta.bg, borderColor: meta.border }
    : { borderColor: 'var(--app-border)', background: 'var(--app-surface)', color: 'var(--app-text)' };

  const interactive = onClick
    ? 'cursor-pointer transition-transform duration-150 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500'
    : '';

  return (
    <Element
      type={onClick ? 'button' : undefined}
      onClick={onClick || undefined}
      className={`w-full rounded-lg border p-4 text-left shadow-sm ${interactive} ${urgent ? 'zz-pulse' : ''} ${
        active ? 'ring-2 ring-primary-500 ring-offset-1' : ''
      }`}
      style={tint}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
          <div className="nums mt-1 text-3xl font-extrabold leading-none">{value}</div>
          {hint ? <div className="mt-1.5 truncate text-xs opacity-70">{hint}</div> : null}
        </div>
        {icon ? <div className="shrink-0 opacity-80">{icon}</div> : null}
      </div>
    </Element>
  );
}
